import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Home, LayoutDashboard, ShoppingCart, User } from "lucide-react";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { useMenusContext } from "@core/helpers/client/contexts/MenusContext";
import { openAuthModal } from "@/hooks/useAuthModal";
import useFixedBarHeight, { FOOTER_NAV_HEIGHT_VAR } from "@/hooks/useFixedBarHeight";
import { normalize_blog_url } from "@/helpers/blogUrl";
import FooterNavigationServer from "./index-server";
import { FOOTER_NAV_BAR_CLASS, footer_nav_item_class } from "../footerNavStyles";

const FooterNavigationClient = () => {
    const { menus, menus_loading } = useMenusContext();
    const barRef = useFixedBarHeight(FOOTER_NAV_HEIGHT_VAR);
    const pathname = usePathname();
    const router = useRouter();
    const { cart, is_auth } = useAuthContext();
    const badge = cart?.products?.reduce((a, b) => a + (b?.count || 0), 0);
    const navigationItems = menus?.["footer-navigation-mobile"] || [];

    const handleAccountClick = (e) => {
        e.preventDefault();
        if (is_auth) {
            router.push("/account/");
        } else {
            openAuthModal("/account/");
        }
    };

    const FooterIcons = {
        home: {
            fill: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H15.5V16H8.5V22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20L3 9Z" fill="#171717" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>,
            outline: <Home size={24} />,
        },

        category: {
            fill: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 3H20C20.5523 3 21 3.44772 21 4V7C21 7.55228 20.5523 8 20 8H15C14.4477 8 14 7.55228 14 7V4C14 3.44772 14.4477 3 15 3Z" fill="#171717" />
                <path d="M15 12H20C20.5523 12 21 12.4477 21 13V20C21 20.5523 20.5523 21 20 21H15C14.4477 21 14 20.5523 14 20V13C14 12.4477 14.4477 12 15 12Z" fill="#171717" />
                <path d="M4 3H9C9.55228 3 10 3.44772 10 4V11C10 11.5523 9.55228 12 9 12H4C3.44772 12 3 11.5523 3 11V4C3 3.44772 3.44772 3 4 3Z" fill="#171717" />
                <path d="M4 16H9C9.55228 16 10 16.4477 10 17V20C10 20.5523 9.55228 21 9 21H4C3.44772 21 3 20.5523 3 20V17C3 16.4477 3.44772 16 4 16Z" fill="#171717" />
                <path d="M15 3H20C20.5523 3 21 3.44772 21 4V7C21 7.55228 20.5523 8 20 8H15C14.4477 8 14 7.55228 14 7V4C14 3.44772 14.4477 3 15 3Z" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M15 12H20C20.5523 12 21 12.4477 21 13V20C21 20.5523 20.5523 21 20 21H15C14.4477 21 14 20.5523 14 20V13C14 12.4477 14.4477 12 15 12Z" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M4 3H9C9.55228 3 10 3.44772 10 4V11C10 11.5523 9.55228 12 9 12H4C3.44772 12 3 11.5523 3 11V4C3 3.44772 3.44772 3 4 3Z" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M4 16H9C9.55228 16 10 16.4477 10 17V20C10 20.5523 9.55228 21 9 21H4C3.44772 21 3 20.5523 3 20V17C3 16.4477 3.44772 16 4 16Z" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>,

            outline: (
                <LayoutDashboard size={24} />
            ),
        },

        cart: {
            fill: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.1198 7.0498L6.7098 14.4698C6.80738 14.9247 7.06047 15.3313 7.42552 15.6197C7.79056 15.908 8.24471 16.0602 8.7098 16.0498H18.4898C18.945 16.0491 19.3863 15.8931 19.7408 15.6076C20.0954 15.3222 20.3419 14.9243 20.4398 14.4798L22.0898 7.0498H5.1198Z" fill="#171717" />
                <path d="M2.0498 2.0498H4.0498L6.7098 14.4698M6.7098 14.4698C6.80738 14.9247 7.06048 15.3313 7.42552 15.6197C7.79056 15.908 8.24471 16.0602 8.7098 16.0498H18.4898C18.945 16.0491 19.3863 15.8931 19.7408 15.6076C20.0954 15.3222 20.3419 14.9243 20.4398 14.4798L22.0898 7.0498H5.1198L6.7098 14.4698ZM8.99976 21C8.99976 21.5523 8.55204 22 7.99976 22C7.44747 22 6.99976 21.5523 6.99976 21C6.99976 20.4477 7.44747 20 7.99976 20C8.55204 20 8.99976 20.4477 8.99976 21ZM19.9998 21C19.9998 21.5523 19.552 22 18.9998 22C18.4475 22 17.9998 21.5523 17.9998 21C17.9998 20.4477 18.4475 20 18.9998 20C19.552 20 19.9998 20.4477 19.9998 21Z" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>,
            outline: <ShoppingCart size={24} strokeWidth={2} />,
        },

        blog: {
            fill: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="black" stroke="black" strokeWidth="2" stroke-miterlimit="10" />
                <path d="M12.9004 9.10001V16C12.9004 16 13.0004 18 11.1004 18.1H9.40039V17.1H10.7004C10.7004 17.1 11.5004 17.1 11.5004 16.1V9.10001" fill="white" />
                <path d="M11.5996 7.39999V5.89999H12.8996V8.29999H9.09961V7.39999H11.5996Z" fill="white" />
            </svg>,
            outline: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#737373" strokeWidth="2" />
                    <path d="M12.9004 9.10001V16C12.9004 16 13.0004 18 11.1004 18.1H9.40039V17.1H10.7004C10.7004 17.1 11.5004 17.1 11.5004 16.1V9.10001" fill="#737373" />
                    <path d="M11.5996 7.39999V5.89999H12.8996V8.29999H9.09961V7.39999H11.5996Z" fill="#737373" />
                </svg>
            ),
        },

        profile: {
            fill: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" fill="#171717" />
                <path d="M19 21V19C19 17.9391 18.5786 16.9217 17.8284 16.1716C17.0783 15.4214 16.0609 15 15 15H9C7.93913 15 6.92172 15.4214 6.17157 16.1716C5.42143 16.9217 5 17.9391 5 19V21H19Z" fill="#171717" />
                <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19 21V19C19 17.9391 18.5786 16.9217 17.8284 16.1716C17.0783 15.4214 16.0609 15 15 15H9C7.93913 15 6.92172 15.4214 6.17157 16.1716C5.42143 16.9217 5 17.9391 5 19V21H19Z" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>,
            outline: <User size={24} strokeWidth={2} />,
        },
    };

    return (
        menus_loading ? <FooterNavigationServer />
            : <div ref={barRef} className={FOOTER_NAV_BAR_CLASS}>
                {navigationItems.map((item) => {
                    const targetUrl = item.icon === "blog"
                        ? normalize_blog_url(item.link?.url)
                        : item.link?.url || "#";
                    const isCurrentActive = pathname === targetUrl || (targetUrl !== "/" && pathname.startsWith(targetUrl));
                    const iconKey = item.icon;

                    const IconComponent = isCurrentActive
                        ? FooterIcons[iconKey]?.fill
                        : FooterIcons[iconKey]?.outline;

                    if (iconKey === "profile") {
                        return (
                            <button
                                key={item.id}
                                onClick={handleAccountClick}
                                className={footer_nav_item_class(isCurrentActive)}
                            >
                                {IconComponent}
                                <span>{item.title}</span>
                            </button>
                        );
                    }

                    const isBlogTab = iconKey === "blog";

                    return (
                        <Link
                            key={item.id}
                            href={targetUrl}
                            {...(isBlogTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            className={footer_nav_item_class(isCurrentActive)}
                        >
                            {IconComponent}

                            {iconKey === "cart" && badge > 0 && (
                                <div className="flex items-center justify-center absolute -top-2 right-1.5 w-5 h-5 bg-jmt-primary-700 border border-jmt-neutral-100 rounded-md">
                                    <span className="text-jmt-white text-[10px]">{badge}</span>
                                </div>
                            )}

                            <span>{item.title}</span>
                        </Link>
                    );
                })}
            </div>
    );
};

export default FooterNavigationClient;
